// @flow
import * as React from 'react';
import styled from 'styled-components';
import _ from 'lodash';


import Table, { TBody, THead, Tr, Th, Td } from '../../components/TableX';
import ChangeIndicatorUp from '../../components/Icons/ChangeIndicatorUp';
import ChangeIndicatorDown from '../../components/Icons/ChangeIndicatorDown';
import ChangeIndicatorHold from '../../components/Icons/ChangeIndicatorHold';
import TeamUserAvatar from '../../components/Team/TeamUserAvatar';
import { below, above } from '../../assets/css/media';
import colors from '../../assets/css/colors';
import PlateWinnerTrophyImg from '../../assets/images/trophy.svg';
import WinnerTrophyImg from '../../assets/images/trophy-colored.svg';
import { isRoundComplete } from '../../helpers/rounds';

const LadderTable = styled(Table)`
	box-shadow: none;
	margin-top: 20px;
	margin-bottom: 50px;
`;

const LadderTHead = styled(THead)`
	background-color: transparent;
`;

const LadderRow = styled(Tr)`
	height: 60px;

	${TBody} & {
		${({ makeFinals }) => !makeFinals && `background: ${colors.silver_grey} !important;`}
	}
`;

const LadderTh = styled(Th)`
	color: #777777;
	font-size: 12px;
	line-height: 10px;
	text-align: center;
`;

const LadderThHideSmall = styled(LadderTh)`
	${below.tablet`
		display: none;
	`}
`;

const LadderThSortable = styled(LadderTh)`
	cursor: pointer;
`;

const LadderThSortableHideSmall = styled(LadderThSortable)`
	${below.tablet`
		display: none;
	`}
`;

const TdStat = styled(Td)`
	position: relative;
	color: ${colors.title};
	&.active {
		background: ${colors.active_stats};
	}
`;

const TdStatHideSmall = styled(TdStat)`
	${below.tablet`
		display: none;
	`}
`;

const TeamUserAvatarStyled = styled(TeamUserAvatar)`
	width: 40px;
`;

const TeamName = styled.div`
	font-size: 12px;
	line-height: 1.125;
	font-weight: bold;
	text-align: left;
	color: ${colors.nrlDarkGreen};
	text-overflow: ellipsis;
	overflow: hidden;
	max-width: 60px;

	${above.phone`
		font-size: 14px;
		max-width: 150px;
	`}

	${above.tablet`
		max-width: 100px;
	`}

	${above.desktop`
		max-width: 200px;
	`}
`;

const UserName = styled.div`
	font-size: 12px;
	line-height: 1.28;
	text-align: left;
	color: ${colors.darkGrey};
	text-overflow: ellipsis;
	overflow: hidden;
	max-width: 60px;

	${above.phone`
		max-width: 150px;
	`}

	${above.tablet`
		max-width: 100px;
	`}

	${above.desktop`
		max-width: 200px;
	`}

	${below.phone`
		display: none;
	`}
`;

const PositionWrapper = styled.div`
	display: flex;
	justify-content: center;
`;

const Rank = styled.div``;

const ChangeIndicator = styled.div`
	display: flex;
	margin-left: 10px;
	padding-top: 2px;
`;

type TableColumns = Array<{ key: string, prop: string, hide_small?: boolean }>;

type Props = {
	compact?: boolean,
	user_id: number,
	ladder_stats: TableColumns,
	ladder_teams_ordered: Array<Object>,
	ladder_teams_by_id: Object,
	rounds_by_id: Object,
	selected_round_id: number,
	league: Object,
	league_current_round_fixtures: Array<Array<number>>,
	show_finals_trophy?: boolean,
  league_next_round_fixtures: Array<Array<number>>
}

type State = {
	order_by_desc: boolean,
	sort_by?: string,
	ladder_sorted: Array<Object>
}

class Ladder extends React.Component<Props, State> {
	state = {
		ladder_sorted: [],
		sort_by: 'league_points_selected_round',
		order_by_desc: false,
	}

	static defaultProps = {
		ladder_stats: [
			{ key: 'Played', prop: 'games_played', hide_small: true },
			{ key: 'Points', prop: 'league_points_selected_round' },
			{ key: 'Win', prop: 'win', hide_small: true },
			{ key: 'Drawn', prop: 'draw', hide_small: true },
			{ key: 'Lost', prop: 'loss', hide_small: true },
			{ key: 'For', prop: 'points_for_selected_round', hide_small: true },
			{ key: 'Against', prop: 'points_against_selected_round', hide_small: true },
			{ key: 'Diff.', prop: 'points_diff_selected_round' },
		]
	}

	componentDidMount() {
		const { ladder_teams_ordered } = this.props;
		this.setState({ ladder_sorted: ladder_teams_ordered });
	}

	componentDidUpdate(prev_props: Props) {
		const { ladder_teams_ordered } = this.props;

		if (ladder_teams_ordered !== prev_props.ladder_teams_ordered) {
			this.setState({ ladder_sorted: ladder_teams_ordered });
		}
	}

	get gf_round_id() {
		const { league } = this.props;
		return _.get(league, 'end_round');
	}

	get is_gf_round() {
		const { selected_round_id } = this.props;
		return this.gf_round_id === selected_round_id;
	}

	get is_gf_complete() {
		const { rounds_by_id } = this.props;
		const final_round = _.get(rounds_by_id, this.gf_round_id);
		return final_round ? isRoundComplete(final_round) : false;
	}

	get show_trophy() {
		const { show_finals_trophy } = this.props;
		return show_finals_trophy && this.is_gf_complete && this.is_gf_round;
	}

	get fixture_winner_id() {
		const { league } = this.props;
		return _.get(league, 'fixture_winner');
	}

	get fixture_plate_winner_id() {
		const { league } = this.props;
		return _.get(league, 'fixture_plate_winner');
	}

	isActiveTab(sort_prop: string){
		const { sort_by } = this.state;
		return _.eq(sort_by, sort_prop) ? 'active' : '';
	}

	onSortColumnChange(column_value: string) {
		this.setState(state => {
			const order_by_desc = _.eq(state.sort_by, column_value) ?
				!state.order_by_desc :
				state.order_by_desc;
			let ladder_sorted;

			if (order_by_desc) {
				ladder_sorted = [
					...state.ladder_sorted.sort((a, b) => (b[column_value] - a[column_value]))
				];
			}
			else {
				ladder_sorted = [
					...state.ladder_sorted.sort((a, b) => (a[column_value] - b[column_value]))
				];
			}

			return {
				ladder_sorted,
				sort_by: column_value,
				order_by_desc,
			};

		});
	}

	renderRankChangeIndicator(team: Object) {
		const { rounds_by_id, selected_round_id } = this.props;
		const round = rounds_by_id[selected_round_id];

		if (round) {
			if (team.rank_history[(round.id)] < team.rank_history[(round.id - 1)]) {
				return <ChangeIndicatorUp />;
			}
			else if (team.rank_history[(round.id)] > team.rank_history[(round.id - 1)]) {
				return <ChangeIndicatorDown />;
			}
		}

		return <ChangeIndicatorHold />;
	}

	renderTrophySection = (team: Object) => {
		const { show_finals_trophy } = this.props;
		const is_plate_winner = _.get(team, 'id') === this.fixture_plate_winner_id;
		const is_winner = _.get(team, 'id') === this.fixture_winner_id;

		if (show_finals_trophy && is_winner) {
			return <TdStat><img src={WinnerTrophyImg} alt="Winner Trophy" /></TdStat>;
		}

		if (show_finals_trophy && is_plate_winner) {
			return <TdStat><img src={PlateWinnerTrophyImg} alt="Plate Winner Trophy" /></TdStat>;
		}

		return <TdStat />;
	}

	renderTableHeader() {
		const { ladder_stats } = this.props;
		const ladder_header_cells = ladder_stats.map(({ key, prop, hide_small }) => {
			const TableHeaderCellComponent = hide_small ?
				LadderThSortableHideSmall : LadderThSortable;

			return (
				<TableHeaderCellComponent
					className={this.isActiveTab(prop)}
					onClick={() => this.onSortColumnChange(prop)}
					key={key}
				>
					{key}
				</TableHeaderCellComponent>
			);
		});

		return (
			<LadderTHead>
				<Tr>
					<LadderThSortable
						className={this.isActiveTab('rank_selected_round')}
						onClick={() => this.onSortColumnChange('rank_selected_round')}
					>
						Pos
					</LadderThSortable>
					<LadderTh>Team</LadderTh>
					<LadderTh></LadderTh>
					{this.show_trophy ? <LadderTh></LadderTh> : null}
					{ ladder_header_cells }
					<LadderThHideSmall>Form</LadderThHideSmall>
					<LadderThHideSmall>Next</LadderThHideSmall>
				</Tr>
			</LadderTHead>
		);
	}

	renderTableRow(team: Object) {
		const {
			ladder_stats,
			ladder_teams_by_id,
			league,
			league_next_round_fixtures
		} = this.props;
		const matchup = league_next_round_fixtures
			.find(fixture => fixture.includes(team.id));
		const opponent = matchup ? ladder_teams_by_id[matchup
			.find(team_id => team_id !== team.id)]: null;
		const is_make_finals = team.rank_selected_round <= league.finals_format
			|| league.finals_format === 0;

		const ladder_stats_body_cells = ladder_stats.map(({ key, prop, hide_small }) => {
			const TableBodyCellComponent = hide_small ? TdStatHideSmall : TdStat;
			return (
				<TableBodyCellComponent
					className={this.isActiveTab(prop)}
					key={prop}
				>
					{team[prop] || '0'}
				</TableBodyCellComponent>
			);
		});
    
		return (
			<LadderRow key={team.id} makeFinals={is_make_finals}>
				<TdStat width="60px" className={`${this.isActiveTab('rank_selected_round')}`}>
					<PositionWrapper>
						<Rank>{team.rank_selected_round}</Rank>
						<ChangeIndicator>
							{this.renderRankChangeIndicator(team)}
						</ChangeIndicator>
					</PositionWrapper>
				</TdStat>
				<TdStat width="40px">
					<TeamUserAvatarStyled
						user_id={team.user_id}
						avatar_version={team.avatar_version}
					/>
				</TdStat>
				<TdStat>
					<TeamName>{team.name}</TeamName>
					<UserName>{team.firstname} {team.lastname}</UserName>
				</TdStat>
				{this.show_trophy ? this.renderTrophySection(team) : null}
				{ladder_stats_body_cells}
				<TdStatHideSmall>
					{team.form}
				</TdStatHideSmall>
				<TdStatHideSmall width="40px">
					{opponent ? (
						<TeamUserAvatarStyled
							user_id={opponent.user_id}
							avatar_version={opponent.avatar_version}
						/>
					) : null}
				</TdStatHideSmall>
			</LadderRow>
		);
	}

	renderTableBodyCompact() {
		const { user_id, league } = this.props;
		const { ladder_sorted } = this.state;

		const ladder_sorted_makes_finals = ladder_sorted.slice(0, league.finals_format || 8);
		const user_team = ladder_sorted.find(team => team.user_id === user_id);
		const is_user_team_makes_finals =
			user_team && ladder_sorted_makes_finals.some(team => team.id === user_team.id);


		return (
			<TBody>
				{ladder_sorted_makes_finals.map(team => this.renderTableRow(team))}
				{!is_user_team_makes_finals && user_team ? this.renderTableRow(user_team) : null}
			</TBody>
		);
	}

	renderTableBody() {
		const { ladder_sorted } = this.state;

		return (
			<TBody>
				{ladder_sorted.map(team => this.renderTableRow(team))}
			</TBody>
		);
	}

	render() {
		const { compact } = this.props;

		return (
			<LadderTable>
				{this.renderTableHeader()}
				{compact ? this.renderTableBodyCompact() : this.renderTableBody()}
			</LadderTable>
		);
	}
}

export default Ladder;


// WEBPACK FOOTER //
// ./src/components/Ladder/index.js